import type {AssistantConversationScrollState} from "./use-assistant-conversation-scroll";

import {TriangleExclamation} from "@gravity-ui/icons";
import {Button, ChatConversation, ChatLoader} from "@project/design-system-web";
import {useI18n} from "@project/i18n/react";
import {useEffect, useState} from "react";

import {aiAssistantMessages} from "../ai-assistant.messages";

interface AssistantBottomAffordanceProps {
  hasConversationBlocker: boolean;
  isDetachedFromBottom: boolean;
  isRunning: boolean;
  onPress: () => void;
  scrollState: AssistantConversationScrollState;
}

type AssistantBottomAffordanceMode =
  "action-required" | "none" | "scroll-button" | "streaming-dots";

const AFFORDANCE_RENDER_DELAY_MS = 700;

export function AssistantBottomAffordance({
  hasConversationBlocker,
  isDetachedFromBottom,
  isRunning,
  onPress,
  scrollState,
}: AssistantBottomAffordanceProps) {
  const {t} = useI18n();
  const shouldShowCustomAffordance =
    scrollState.hasOverflow && !scrollState.isAtBottom && isDetachedFromBottom;
  const affordanceMode: AssistantBottomAffordanceMode = shouldShowCustomAffordance
    ? hasConversationBlocker
      ? "action-required"
      : isRunning
        ? "streaming-dots"
        : "scroll-button"
    : "none";
  const [visibleMode, setVisibleMode] = useState<AssistantBottomAffordanceMode>("none");

  useEffect(() => {
    if (affordanceMode === visibleMode) return;

    setVisibleMode("none");

    if (affordanceMode === "none") return;

    const timeoutId = window.setTimeout(() => {
      setVisibleMode(affordanceMode);
    }, AFFORDANCE_RENDER_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [affordanceMode, visibleMode]);

  if (visibleMode === "action-required") {
    return (
      <div className="chat-conversation__scroll-button-container">
        <Button
          size="sm"
          variant="secondary"
          className="border-warning/60 bg-warning/20 text-warning hover:bg-warning/25 border shadow-sm"
          onPress={onPress}
        >
          <TriangleExclamation className="size-4 opacity-100" />
          {t(aiAssistantMessages.actionRequired)}
        </Button>
      </div>
    );
  }

  if (visibleMode === "streaming-dots") {
    return (
      <div className="chat-conversation__scroll-button-container">
        <button
          type="button"
          aria-label={t(aiAssistantMessages.scrollToStreamingResponse)}
          className="bg-background/95 border-border flex h-8 cursor-pointer items-center rounded-full border px-4 shadow-sm"
          onClick={onPress}
        >
          <ChatLoader.Dots className="chat-loader__dots--sm" />
        </button>
      </div>
    );
  }

  if (visibleMode === "scroll-button") {
    return (
      <ChatConversation.ScrollButton
        aria-label={t(aiAssistantMessages.scrollToBottom)}
        tooltip={t(aiAssistantMessages.scrollToBottom)}
      />
    );
  }

  return null;
}
